import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ActiveCarpoolRidesLayer — Phase D live-driver overlay.
 *
 * Renders each assigned/en_route ride as:
 *   • a clay-ember polyline from driver origin → pickup (OSRM geometry)
 *   • a deep-ink pin at the pickup
 *   • a moving clay-ember dot at the driver's current position
 *
 * Updates come from useCarpoolRequests().active, which is fed by the
 * /ws/carpool WebSocket. driver_position events arrive every ~800 ms; the
 * marker retargets each frame and Leaflet handles the transform.
 */
import { Marker, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
// Clay-ember filled dot with a white halo — reads as "the vehicle" without
// competing with the bus icons for attention.
function driverDotIcon() {
    return L.divIcon({
        className: "bb-active-carpool-driver",
        html: `
      <div style="position:relative;width:22px;height:22px;">
        <span style="
          position:absolute;inset:-6px;border-radius:50%;
          border:2px solid #bc7155;opacity:.35;
          animation:bb-ping 1.6s ease-in-out infinite;
        "></span>
        <span style="
          position:absolute;inset:0;width:22px;height:22px;border-radius:50%;
          background:#bc7155;border:3px solid #ffffff;
          box-shadow:0 0 0 1px #bc7155;
        "></span>
      </div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
    });
}
// Small hollow pickup marker (deep-ink) — used only when the ride is solo.
function pickupIcon() {
    return L.divIcon({
        className: "bb-active-carpool-pickup",
        html: `
      <div style="
        width:12px;height:12px;border-radius:50%;
        background:#ffffff;border:2px solid #000d10;
      "></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
    });
}
// Phase E — numbered pickup pin for a pooled ride. `primary=true` uses a
// filled clay-ember disk; secondary is hollow. Renders the pickup order
// (1 or 2) inside so the audience can see the driver's route sequence.
function numberedPoolPin(order, primary) {
    const fill = primary ? "#bc7155" : "#ffffff";
    const fg = primary ? "#ffffff" : "#bc7155";
    return L.divIcon({
        className: `bb-active-carpool-pool-pin ${primary ? "primary" : "secondary"}`,
        html: `
      <div style="
        width:22px;height:22px;border-radius:50%;
        background:${fill}; color:${fg};
        border:2px solid #bc7155;
        box-shadow:0 0 0 2px #ffffff;
        font: 800 12px/22px 'Inter', sans-serif;
        text-align:center;
      ">${order}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
    });
}
export function ActiveCarpoolRidesLayer({ rides, }) {
    // Phase E — dedupe by driver_id. A pooled driver appears in `rides`
    // twice (once per rider record) but should render as a single polyline
    // + one driver dot + N numbered pickups.
    const byDriver = new Map();
    for (const r of rides) {
        const did = r.driver?.id;
        if (!did)
            continue;
        // Prefer whichever record has pool_pickups populated (so we render
        // the numbered pins correctly).
        const existing = byDriver.get(did);
        if (existing == null ||
            (r.driver?.pool_pickups?.length ?? 0) >
                (existing.driver?.pool_pickups?.length ?? 0)) {
            byDriver.set(did, r);
        }
    }
    return (_jsx(_Fragment, { children: Array.from(byDriver.values()).map((r) => {
            const d = r.driver;
            if (!d)
                return null;
            const pos = d.position ?? d.origin;
            const linePositions = d.polyline.map((p) => [p.lat, p.lng]);
            const remaining = d.remaining_min != null ? d.remaining_min : d.eta_min;
            const pool = d.pool_pickups ?? [];
            const isPool = pool.length >= 2;
            return (_jsxs("div", { style: { display: "contents" }, children: [linePositions.length >= 2 ? (_jsx(Polyline, { positions: linePositions, pathOptions: {
                            color: "#bc7155",
                            // Pool rides get a beefier polyline to stand out.
                            weight: isPool ? 4.5 : 3,
                            opacity: isPool ? 0.85 : 0.7,
                        } })) : null, isPool ? (pool.map((p, idx) => (_jsx(Marker, { position: [p.pickup.lat, p.pickup.lng], icon: numberedPoolPin(p.order ?? idx + 1, idx === 0), interactive: false }, `${d.id}-${p.request_id}`)))) : (_jsx(Marker, { position: [r.pickup.lat, r.pickup.lng], icon: pickupIcon(), interactive: false })), _jsx(Marker, { position: [pos.lat, pos.lng], icon: driverDotIcon(), children: _jsx(Tooltip, { direction: "top", offset: [0, -12], children: isPool
                                ? `${d.name} · POOL · ${remaining.toFixed(1)} min · 2 riders`
                                : `${d.name} · ${d.vehicle} · ${remaining.toFixed(1)} min to ${r.pickup.label ?? "pickup"}` }) })] }, d.id));
        }) }));
}
